import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import type { StorefrontView } from '~/schemas/storefront-view'

export const VIEW_QUERY_KEY = 'view'

/**
 * Storefront view state for the club and fixture pages (BOAT-1912).
 *
 * The view lives ONLY in the URL (`?view=<slug>`): pages append it to the
 * fixture links they render so it follows the visitor, and every other link
 * (breadcrumbs, header nav) drops it on purpose so the rest of the catalogue
 * stays discoverable. Nothing is persisted.
 *
 * @param views the views the API says are available on this page/fixture
 */
export function useStorefrontView(views: MaybeRefOrGetter<StorefrontView[] | undefined | null>) {
  const route = useRoute()

  const available = computed(() => toValue(views) ?? [])

  const requestedSlug = computed(() => {
    const raw = route.query[VIEW_QUERY_KEY]
    return typeof raw === 'string' && raw !== '' ? raw : null
  })

  // Only a view the API offers here counts; an unknown or stale slug renders
  // the default page as if the param were absent.
  const activeView = computed(
    () => available.value.find((view) => view.slug === requestedSlug.value) ?? null,
  )

  const isViewActive = computed(() => activeView.value !== null)

  // Views to offer a banner for on the current rendering: all of them on the
  // default page, none while a view is active (that page offers the way back).
  const offeredViews = computed(() => (isViewActive.value ? [] : available.value))

  const viewQuery = computed(() =>
    activeView.value ? `?${VIEW_QUERY_KEY}=${encodeURIComponent(activeView.value.slug)}` : '',
  )

  /** Same path, in the given view. */
  const hrefForView = (view: StorefrontView, path: string = route.path) =>
    `${path}?${VIEW_QUERY_KEY}=${encodeURIComponent(view.slug)}`

  /** Same path, default view (the param stripped; other params dropped too). */
  const exitHref = computed(() => route.path)

  return {
    available,
    activeView,
    isViewActive,
    offeredViews,
    viewQuery,
    hrefForView,
    exitHref,
  }
}
